import {
  CONFIG_OPTION_TOAST_ERROR,
  CONFIG_OPTION_TOAST_NORMAL,
} from "common/toast";
import en from "date-fns/locale/en-US";
import ko from "date-fns/locale/ko";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import "react-quill/dist/quill.snow.css";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Col,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Progress,
  Row,
  Spinner,
} from "reactstrap";
import * as Yup from "yup";

//import images
import { UploadProps } from "antd";
import DropdownStatusNew from "components/Common/DropdownStatusNew";
import {
  COLOR_STICKER_STATUS,
  LIST_OPTION_BAR_DETAIL_REVIEW_STATUS,
} from "helpers/constans";
import UploadImageSingle from "components/Common/UploadImageSingle";
import { getSticker, postSticker, putSticker } from "store/sticker/thunk";

export interface Props {
  id?: string;
  triggerRefresh?: () => void;
  setLinkImage?: any;
  onCloseClick?: any;
  stickerPackageId?: string;
}

registerLocale("en", en);
registerLocale("ko", ko);

const uploadProps: UploadProps = {
  name: "files",
  accept: "image/png,image/jpeg,image/*",
  beforeUpload: () => false,
  headers: {
    authorization: "authorization-text",
  },
};

const StickerForm = ({
  id = "",
  triggerRefresh,
  setLinkImage,
  onCloseClick,
  stickerPackageId
}: Props) => {
  const { t, i18n } = useTranslation();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState<boolean>(false);

  const handleSubmit = async (values: any) => {
    if(!stickerPackageId) return;

    try {
      setIsLoading((_prev) => true);
      const data = {
        name: values?.name,
        description: values?.description,
        order: values?.order,
        url: values?.url,
        stickerPackageId: stickerPackageId,
        status: values?.status
      };

      const response: any = id
        ? await putSticker(id, data)
        : await postSticker(data);

      if (response?.data) {
        setIsLoading((_prev) => false);
        toast(
          `${t("The process has been completed.")}`,
          CONFIG_OPTION_TOAST_NORMAL
        );
        triggerRefresh && triggerRefresh();
      } else {
        setIsLoading((_prev) => false);
        toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
      }
    } catch (error: any) {
      setIsLoading((_prev) => false);
      toast(`${error?.message || ""}`, CONFIG_OPTION_TOAST_ERROR);
      return error;
    }
  };

  const validationSchema = Yup.object({
    name: Yup.string().required(`${t("Name is required")}`),
    url: Yup.string().required(`${t("Image is required")}`),
  });

  const formik = useFormik({
    initialValues: {
      name: "",
      order: 0,
      status: 1,
      url: null,
    },
    validationSchema,
    onSubmit: handleSubmit,
  });

  const handleSetValueForm = (valueDefault: any) => {
    formik.setValues(valueDefault);
  };

  const handleCallStickerDetail = async (idItem: string) => {
    try {
      if (!idItem) {
        return;
      }
      setIsLoadingDetail((_prev) => true);
      const resDetail: any = await getSticker(idItem);

      if (resDetail?.data) {
        handleSetValueForm(resDetail?.data);
        setIsLoadingDetail((_prev) => false);
      }
    } catch (error: any) {
      setIsLoadingDetail((_prev) => false);
      return error;
    }
  };

  useEffect(() => {
    handleCallStickerDetail(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    document.title = `${t("Create Sticker")} - ${t(
      "Sticker Management"
    )} | Baive`;
    document.body.classList.remove("vertical-sidebar-enable");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n?.language]);

  return (
    <React.Fragment>
      <Modal isOpen={true} centered={true} scrollable={false} size="l">
        <ModalHeader toggle={isLoading ? () => {} : onCloseClick}>
          {id ? t("Button Update Sticker") : t("Button Create Sticker")}
        </ModalHeader>
        <ModalBody className="py-3 px-4">
          <div className="mb-4">
            <Progress
              value={
                100 - (100 / 2) * Number(Object.keys(formik.errors).length)
              }
              color="primary"
              className="animated-progress progress-sm"
            />
          </div>

          <div className="mt-3">
            <form onSubmit={formik.handleSubmit}>
              <Row>
                <Col lg={12}>
                  <div className="mb-3">
                    <Label htmlFor="nameBar" className="form-label">
                      {t("Name")} <span className="text-danger">*</span>
                    </Label>
                    <Input
                      id="nameBar"
                      name="name"
                      type="text"
                      className="form-control"
                      placeholder={t("Enter your name")}
                      value={formik?.values?.name}
                      onChange={(event: any) =>
                        formik.setFieldValue("name", event?.target?.value)
                      }
                    />
                    {formik.touched.name && formik.errors.name ? (
                      <div className="text-danger mt-2">{formik.errors.name}</div>
                    ) : null}
                  </div>
                </Col>

                <Col lg={12}>
                  <div className="mb-3">
                    <Label htmlFor="order" className="form-label">
                      {t("Order")}
                    </Label>
                    <Input
                      name="order"
                      type="number"
                      className="form-control"
                      placeholder={t("Enter order")}
                      value={formik?.values?.order}
                      onChange={(event: any) =>
                        formik.setFieldValue("order", event?.target?.value)
                      }
                    />
                    {formik.touched.order && formik.errors.order ? (
                      <div className="text-danger mt-2">{formik.errors.order}</div>
                    ) : null}
                  </div>
                </Col>

                <Col lg={12}>
                  <div className="mb-3">
                    <Label className="form-label">{t("Status")}</Label>

                    <DropdownStatusNew
                      name="status"
                      dataList={LIST_OPTION_BAR_DETAIL_REVIEW_STATUS || []}
                      placeholder={`${t("Status")}...`}
                      className="dropdown-status-rounded"
                      classNamePrefix="name-prefix"
                      initialValue={formik?.values?.status}
                      onChangeSelect={(event: any) =>
                        formik.setFieldValue("status", event?.value)
                      }
                      isHasOptionAll={false}
                      optionAll={{ label: t("All Filter"), value: "" }}
                      colors={COLOR_STICKER_STATUS}
                    />

                    {formik?.touched?.status && formik?.errors?.status ? (
                      <div className="text-danger mt-2">
                        {/* {formik?.errors?.is_active} */}
                      </div>
                    ) : null}
                  </div>
                </Col>

                <Col xxl={12}>
                  <label className="form-label">
                    {t("Image")}
                    <span className="text-danger"> *</span>
                  </label>

                  <UploadImageSingle
                    data={formik.getFieldProps("url")?.value}
                    setLinkImage={setLinkImage}
                    formik={formik}
                    isMultiple={false}
                    uploadProps={uploadProps}
                    formikNameImage={"url"}
                  />
                </Col>

                <Col lg={12}>
                  <div className="hstack gap-2 justify-content-end">
                    {(
                      <button type="submit" className="btn btn-primary fs-14">
                        {isLoading ? (
                          <Spinner size="sm" className="me-2"></Spinner>
                        ) : !!id ? (
                          <i className="ri-login-circle-line align-bottom me-2 "></i>
                        ) : (
                          <i className="ri-add-fill align-bottom me-2"></i>
                        )}
                        {id
                          ? t("Button Update")
                          : t("Button Create")}
                      </button>
                    )}
                  </div>
                </Col>
              </Row>
            </form>
          </div>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default StickerForm;
